import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import Navbar from './Navbar';
import EmployerSidebar, { ChevronDownIcon } from './EmployerSidebar';

const METRIC_CARDS = [
  { key: 'jobs_created', label: 'Jobs Created' },
  { key: 'applications', label: 'Applications' },
  { key: 'interviews', label: 'Interviews' },
  { key: 'offers', label: 'Offers' },
  { key: 'hires', label: 'Hires' },
];

function HiringAnalytics() {
  const [user] = useState(() => {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  });

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [fromDraft, setFromDraft] = useState('');
  const [toDraft, setToDraft] = useState('');
  const [appliedRange, setAppliedRange] = useState({ from: '', to: '' });

  // Export state
  const [exporting, setExporting] = useState(''); // '' | 'pdf' | 'excel' | 'csv' | 'email'
  const [exportMsg, setExportMsg] = useState('');
  const [showExportMenu, setShowExportMenu] = useState(false);

  // Mobile-only: sidebar drawer toggle (same pattern as other employer pages)
  const [showFiltersMobile, setShowFiltersMobile] = useState(false);

  const navigate = useNavigate();

  const fetchAnalytics = useCallback(async (range) => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (range.from) params.from = range.from;
      if (range.to) params.to = range.to;
      const res = await api.get('/analytics/hiring', { params });
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not load hiring analytics.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    fetchAnalytics(appliedRange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApplyRange = (e) => {
    e.preventDefault();
    const range = { from: fromDraft, to: toDraft };
    setAppliedRange(range);
    fetchAnalytics(range);
  };

  const handleResetRange = () => {
    setFromDraft('');
    setToDraft('');
    setAppliedRange({ from: '', to: '' });
    fetchAnalytics({ from: '', to: '' });
  };

  const buildExportParams = () => {
    const params = { type: 'employer' };
    if (appliedRange.from) params.from = appliedRange.from;
    if (appliedRange.to) params.to = appliedRange.to;
    return params;
  };

  const triggerDownload = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleExportPdf = async () => {
    setExporting('pdf');
    setExportMsg('');
    try {
      const res = await api.get('/analytics/export/pdf', {
        params: buildExportParams(),
        responseType: 'blob',
      });
      triggerDownload(res.data, `analytics-employer-${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (err) {
      setExportMsg('Could not generate PDF report.');
    } finally {
      setExporting('');
      setShowExportMenu(false);
    }
  };

  const handleExportExcel = async (format) => {
    setExporting(format);
    setExportMsg('');
    try {
      const res = await api.get('/analytics/export/excel', {
        params: { ...buildExportParams(), format },
        responseType: 'blob',
      });
      const ext = format === 'csv' ? 'csv' : 'xlsx';
      triggerDownload(res.data, `analytics-employer-${new Date().toISOString().slice(0, 10)}.${ext}`);
    } catch (err) {
      setExportMsg(`Could not generate ${format.toUpperCase()} report.`);
    } finally {
      setExporting('');
      setShowExportMenu(false);
    }
  };

  const handleEmailReport = async () => {
    setExporting('email');
    setExportMsg('');
    try {
      const res = await api.post('/analytics/export/email', { type: 'employer' });
      setExportMsg(res.data.message || 'Report emailed successfully.');
    } catch (err) {
      setExportMsg(err.response?.data?.message || 'Could not email report.');
    } finally {
      setExporting('');
      setShowExportMenu(false);
    }
  };

  const hasActiveRange = appliedRange.from || appliedRange.to;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FAF5FF] to-[#FDF2F8]">
      <Navbar user={user} />

      <div className="flex">
        <EmployerSidebar user={user} activeTab="reports" />

        <div className="flex-1 min-w-0 lg:ml-16 lg:peer-hover:ml-64 transition-all duration-200 ease-in-out">
          <div className="max-w-5xl mx-auto px-4 py-6 sm:py-8">
            {/* Mobile-only: nav drawer */}
            <button
              onClick={() => setShowFiltersMobile((s) => !s)}
              className="lg:hidden w-full mb-4 bg-white rounded-xl shadow p-4 flex items-center justify-between font-semibold text-[#1E1033]"
            >
              <span>☰ Menu</span>
              <span className="text-sm text-gray-400 inline-flex items-center gap-1">
                {showFiltersMobile ? 'Hide' : 'Show'}
                <ChevronDownIcon className={`transition-transform ${showFiltersMobile ? 'rotate-180' : ''}`} />
              </span>
            </button>

            {showFiltersMobile && <EmployerSidebar user={user} activeTab="reports" mobile />}

            <div className="flex items-start justify-between flex-wrap gap-3 mb-1">
              <div>
                <h2 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-[#7C3AED] via-[#C026D3] to-[#EC4899] bg-clip-text text-transparent inline-block">Hiring Analytics</h2>
                <p className="text-gray-500 text-sm">
                  {hasActiveRange
                    ? `Showing results from ${appliedRange.from || 'the start'} to ${appliedRange.to || 'today'}`
                    : 'Showing all-time totals across your company'}
                </p>
              </div>

              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowExportMenu((v) => !v)}
                  className="bg-white border border-gray-200 text-[#1E1033] px-4 py-2 rounded-lg font-semibold text-sm shadow hover:bg-purple-50 flex items-center gap-2"
                >
                  Export
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>

                {showExportMenu && (
                  <div className="absolute right-0 mt-2 w-44 bg-white rounded-lg shadow-lg border border-gray-100 py-1 z-10">
                    <button
                      onClick={handleExportPdf}
                      disabled={exporting !== ''}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      {exporting === 'pdf' ? 'Generating…' : 'Download PDF'}
                    </button>
                    <button
                      onClick={() => handleExportExcel('xlsx')}
                      disabled={exporting !== ''}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      {exporting === 'xlsx' ? 'Generating…' : 'Download Excel'}
                    </button>
                    <button
                      onClick={() => handleExportExcel('csv')}
                      disabled={exporting !== ''}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      {exporting === 'csv' ? 'Generating…' : 'Download CSV'}
                    </button>
                    <div className="border-t border-gray-100 my-1" />
                    <button
                      onClick={handleEmailReport}
                      disabled={exporting !== ''}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      {exporting === 'email' ? 'Sending…' : 'Email me this report'}
                    </button>
                  </div>
                )}
              </div>
            </div>

            {exportMsg && (
              <div className="bg-purple-50 text-[#9333EA] border border-purple-100 p-3 rounded-lg mb-4 text-sm mt-3">
                {exportMsg}
              </div>
            )}

            <form onSubmit={handleApplyRange} className="bg-white rounded-xl shadow p-4 sm:p-5 mb-6 mt-4 flex flex-col sm:flex-row items-start sm:items-end gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">From</label>
                <input
                  type="date"
                  value={fromDraft}
                  onChange={(e) => setFromDraft(e.target.value)}
                  className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#9333EA]"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">To</label>
                <input
                  type="date"
                  value={toDraft}
                  onChange={(e) => setToDraft(e.target.value)}
                               className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#9333EA]"
                />
              </div>
              <div className="flex gap-2">
                <button
                  type="submit"
                  className="bg-gradient-to-r from-[#7C3AED] to-[#DB2777] text-white px-4 py-2 rounded-lg font-semibold text-sm hover:opacity-90"
                >
                  Apply
                </button>
                {hasActiveRange && (
                  <button
                    type="button"
                    onClick={handleResetRange}
                    className="text-gray-500 px-3 py-2 text-sm font-medium hover:text-gray-700"
                  >
                    Reset
                  </button>
                )}
              </div>
            </form>

            {error && (
              <div className="bg-red-50 text-red-600 border border-red-100 p-3 rounded-lg mb-4 text-sm">
                {error}
              </div>
            )}

            {loading ? (
              <p className="text-gray-500 text-sm">Loading...</p>
            ) : data ? (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
                  {METRIC_CARDS.map((m) => (
                    <div key={m.key} className="bg-white rounded-xl shadow p-4 sm:p-5">
                      <p className="text-2xl sm:text-3xl font-bold text-[#1E1033]">{data[m.key]}</p>
                      <p className="text-xs sm:text-sm text-gray-500 mt-1">{m.label}</p>
                    </div>
                  ))}
                </div>

                <div className="bg-white rounded-xl shadow p-5 sm:p-6">
                  <div className="flex items-baseline justify-between mb-1">
                    <h3 className="font-bold text-[#1E1033]">Joining Ratio</h3>
                    <p className="text-2xl font-bold text-[#1E1033]">
                      {data.joining_ratio === null ? '—' : `${data.joining_ratio}%`}
                    </p>
                  </div>
                  <p className="text-sm text-gray-500">
                    Of candidates who accepted an offer, the share that actually joined.
                  </p>
                  {data.joining_ratio_detail && (
                    <p className="text-xs text-gray-400 mt-2">
                      {data.joining_ratio_detail.joined} joined out of{' '}
                      {data.joining_ratio_detail.accepted_offers} accepted offers
                      {data.joining_ratio_detail.accepted_offers === 0 && ' — no accepted offers yet'}
                    </p>
                  )}
                </div>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

export default HiringAnalytics;